import { useRef, useState } from "react"
import type { ProfileResponse } from "~/api/auth"
import { getProfile } from "~/api/auth"
import { regeneratePortrait } from "~/api/portrait"
import { PhotoUploadPanel } from "~/components/PhotoUploadPanel"
import { HeroPortraitFrame } from "~/components/HeroPortraitFrame"
import { heroPortraitUrl } from "~/utils/heroPortraitUrl"

interface HeroPortraitCardProps {
  profile: ProfileResponse
  onProfileUpdated: (profile: ProfileResponse) => void
}

export function HeroPortraitCard({ profile, onProfileUpdated }: HeroPortraitCardProps) {
  const dog = profile.dog_profile
  const portrait = dog?.portrait
  const [photo, setPhoto] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const pollRef = useRef<number | null>(null)

  if (!dog || !portrait) return null

  const thumbnailUrl = heroPortraitUrl(dog.archetype_icon, portrait)
  const canChange = portrait.can_regenerate && portrait.status !== "generating"

  async function handleConfirmRegenerate() {
    if (!photo) return
    setSubmitting(true)
    setUploadError(null)
    try {
      await regeneratePortrait(photo)
      setConfirming(false)
      setPhoto(null)
      setPreviewUrl(null)
      startPollingForReady()
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Could not start portrait generation")
    } finally {
      setSubmitting(false)
    }
  }

  function startPollingForReady() {
    if (pollRef.current) window.clearInterval(pollRef.current)
    pollRef.current = window.setInterval(async () => {
      try {
        const data = await getProfile()
        onProfileUpdated(data)
        if (data.dog_profile?.portrait?.status === "ready" || data.dog_profile?.portrait?.status === "failed") {
          if (pollRef.current) window.clearInterval(pollRef.current)
        }
      } catch {
        // ignore
      }
    }, 2000)
  }

  return (
    <section className="mt-6 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4 shadow-sm">
      <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Hero Portrait</h2>
      <p className="mt-1 text-sm font-medium text-[var(--color-text-secondary)]">
        Your personalised Doggy Dynamo portrait
      </p>
      <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
        This portrait was created especially for {dog.name} using the photo you uploaded.
      </p>

      {thumbnailUrl && (
        <HeroPortraitFrame
          className="mt-4"
          src={thumbnailUrl}
          alt={`Personalised portrait for ${dog.name}`}
        />
      )}

      {portrait.status === "generating" && (
        <p className="mt-3 text-sm text-[var(--color-text-secondary)]" role="status">
          Creating your new portrait…
        </p>
      )}

      {portrait.status === "failed" && portrait.failure_message && (
        <p className="mt-3 text-sm text-[var(--color-text-secondary)]" role="status">
          {portrait.failure_message}
        </p>
      )}

      {!confirming ? (
        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            disabled={!canChange}
            onClick={() => setConfirming(true)}
            className="rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Change portrait
          </button>
          {!profile.user.premium && !portrait.complimentary_update_used && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              Freemium accounts include one complimentary portrait update.
            </p>
          )}
          {!canChange && portrait.regeneration_blocked_reason && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              {portrait.regeneration_blocked_reason}
            </p>
          )}
        </div>
      ) : (
        <div className="mt-4">
          <PhotoUploadPanel
            dogName={dog.name}
            photo={photo}
            previewUrl={previewUrl}
            error={uploadError}
            onPhotoSelected={(file, preview) => {
              setPhoto(file)
              setPreviewUrl(preview)
              setUploadError(null)
            }}
            onClear={() => {
              setPhoto(null)
              setPreviewUrl(null)
            }}
          />
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              disabled={!photo || submitting}
              onClick={handleConfirmRegenerate}
              className="flex-1 rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] disabled:opacity-50"
            >
              {submitting ? "Starting…" : "Confirm and generate"}
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirming(false)
                setPhoto(null)
                setPreviewUrl(null)
                setUploadError(null)
              }}
              className="rounded-lg border border-[var(--color-divider)] px-4 py-2 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
